import { type ApiCallOptions, ApiHttpError } from '@/shared/api/client';
import { shouldEscalateToRouteBoundary } from '@/shared/lib/infiniteQuery';
import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { listMySourceImports } from '../api/imports';
import { getIntakeJob } from '../api/intake';
import { listMySubmissions } from '../api/submissions';
import {
  INTAKE_POLL_MS,
  LOCAL_UPLOAD_OFFSET_PROBE_CONCURRENCY,
  LOCAL_UPLOAD_STALL_CHECK_MS,
  LOCAL_UPLOAD_STALL_PROBE_RETRY_MS,
  LOCAL_UPLOAD_STATUS_POLL_MS,
  SOURCE_IMPORT_STATUS_POLL_MS,
  UPLOADS_HISTORY_REFRESH_MS,
  UPLOADS_KEY,
  UPLOADS_STALE_TIME,
} from '../config/constants';
import { deriveLocalStatusPatch, deriveStatusPatch } from '../lib/importProjection';
import {
  type LocalUploadProgressObservations,
  confirmLiveUploadStalls,
  interruptedSessionIds,
  observeGcsOffsets,
  observeProgressOffsets,
  observeReportedProgress,
  sameProgressObservations,
  sameSessionIds,
} from '../lib/localUploadStall';
import { toSourceImportsPage, toSubmissionsPage } from '../lib/transferModels';
import { uploadHistorySignature } from '../lib/uploadHistorySync';
import { isTerminalPhase } from '../lib/uploadLifecycle';
import {
  type LocalSubmissionStatusItem,
  type SourceImport,
  type SourceImportStatusItem,
  type SourceImportsPage,
  type SourceImportsPageWire,
  type Submission,
  type SubmissionsPage,
  type SubmissionsPageWire,
  type TransferHistoryQueryBucket,
  type UploadTask,
  isIntakeTerminal,
} from '../types/uploads';
import { fetchSourceImportsStatus } from './sourceImportService';
import { fetchLocalSubmissionsStatus, getUploadOffset } from './submissionService';

const LOCAL_UPLOAD_STATUS_BATCH_SIZE = 100;
const SOURCE_IMPORT_STATUS_BATCH_SIZE = 200;

/**
 * TanStack Query hooks for ingest STATUS (reads/polling) — distinct from the imperative upload engine,
 * which owns byte transfer. Used by the `/transfers` history page and any component that wants to
 * observe a job/import without driving it. Live queue items are driven by the engine; these polls stop
 * at a terminal state via `refetchInterval`.
 */

function intakeJobQuery(intakeId: string) {
  return queryOptions({
    queryKey: [UPLOADS_KEY, 'intake', intakeId],
    queryFn: ({ signal }) => getIntakeJob(intakeId, { signal }),
    enabled: !!intakeId,
    retry: (failureCount, error) => {
      if (error instanceof ApiHttpError && [401, 403, 404].includes(error.status)) return false;
      return failureCount < 1;
    },
    refetchInterval: (query) => {
      if (query.state.error instanceof ApiHttpError && query.state.error.status === 404)
        return false;
      const state = query.state.data?.state;
      return state && isIntakeTerminal(state) ? false : INTAKE_POLL_MS;
    },
  });
}

export function useIntakeJob(intakeId: string) {
  return useQuery(intakeJobQuery(intakeId));
}

function selectSubmissionPages(
  data: InfiniteData<SubmissionsPageWire, string | undefined>,
): InfiniteData<SubmissionsPage, string | undefined> {
  return { ...data, pages: data.pages.map(toSubmissionsPage) };
}

function selectSourceImportPages(
  data: InfiniteData<SourceImportsPageWire, string | undefined>,
): InfiniteData<SourceImportsPage, string | undefined> {
  return { ...data, pages: data.pages.map(toSourceImportsPage) };
}

/**
 * The caller's own submission sessions — the `/transfers` history source. Exposed as a shared
 * factory so the route loader can prefetch the first page (mirrors the raw-footage asset queries).
 * A 403 stays in query state so the page can render its local no-access state;
 * the route loader therefore prefetches this query best-effort.
 */
export function mySubmissionsQuery(uid: string, bucket?: TransferHistoryQueryBucket) {
  return infiniteQueryOptions({
    queryKey: [UPLOADS_KEY, 'my-submissions', uid, bucket ?? 'all'],
    queryFn: ({ pageParam, signal }) =>
      listMySubmissions(uid, { limit: 100, cursor: pageParam, bucket }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!uid,
    staleTime: UPLOADS_STALE_TIME,
    refetchInterval: UPLOADS_HISTORY_REFRESH_MS,
    // No `placeholderData: keepPreviousData` here, deliberately: the only varying part of this key is
    // `uid`, so a key change is an account switch, not a filter edit — carrying the previous data over
    // would render one account's transfer history under another for a beat.
    throwOnError: (_error, query) =>
      shouldEscalateToRouteBoundary(query.state.error, query.state.data !== undefined),
  });
}

interface LocalUploadOffsetSnapshot {
  observedAt: number;
  offsets: Readonly<Record<string, number>>;
  expiredSessionIds: readonly string[];
}

export interface LocalUploadHealth {
  interruptedSessionIds: ReadonlySet<string>;
  expiredSessionIds: ReadonlySet<string>;
}

function isExpiredUploadOffsetError(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 410;
}

async function fetchLocalUploadOffsets(
  sessionIds: readonly string[],
  options: ApiCallOptions = {},
): Promise<LocalUploadOffsetSnapshot> {
  const offsets: Record<string, number> = {};
  const expiredSessionIds = new Set<string>();
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const sessionId = sessionIds[cursor];
      cursor += 1;
      if (!sessionId) return;
      try {
        offsets[sessionId] = await getUploadOffset(sessionId, options);
      } catch (error) {
        if (options.signal?.aborted) throw error;
        if (isExpiredUploadOffsetError(error)) expiredSessionIds.add(sessionId);
        // Other terminal-state races or transient probe failures remain inconclusive.
      }
    }
  };
  const workerCount = Math.min(LOCAL_UPLOAD_OFFSET_PROBE_CONCURRENCY, sessionIds.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return { observedAt: Date.now(), offsets, expiredSessionIds: [...expiredSessionIds] };
}

/** Detect history-only local uploads whose authoritative GCS committed offset has not advanced for
 * ten minutes. No backend status is mutated: interrupted sessions remain resumable. */
export function useInterruptedLocalUploads(
  uid: string,
  submissions: readonly Submission[],
  excludedSessionIds: ReadonlySet<string>,
): LocalUploadHealth {
  const active = useMemo(
    () =>
      submissions.filter(
        (submission) =>
          (submission.status === 'pending' || submission.status === 'uploading') &&
          !excludedSessionIds.has(submission.sessionId),
      ),
    [excludedSessionIds, submissions],
  );
  const activeSessionIds = useMemo(() => active.map((row) => row.sessionId), [active]);
  const observations = useRef<LocalUploadProgressObservations>(new Map());
  const [interrupted, setInterrupted] = useState<ReadonlySet<string>>(new Set());
  const [probeExpired, setProbeExpired] = useState<ReadonlySet<string>>(new Set());
  const [probeEnabled, setProbeEnabled] = useState(false);

  useEffect(() => {
    observations.current = new Map();
    setInterrupted(new Set());
    setProbeExpired(new Set());
    setProbeEnabled(false);
    if (!uid) return;
    const timer = window.setTimeout(() => setProbeEnabled(true), LOCAL_UPLOAD_STALL_CHECK_MS);
    return () => window.clearTimeout(timer);
  }, [uid]);

  useEffect(() => {
    observations.current = observeReportedProgress(observations.current, active, Date.now());
    const next = interruptedSessionIds(observations.current);
    setInterrupted((previous) => (sameSessionIds(previous, next) ? previous : next));
  }, [active]);

  const probe = useQuery({
    queryKey: [UPLOADS_KEY, 'local-upload-offsets', uid, activeSessionIds],
    queryFn: ({ signal }) => fetchLocalUploadOffsets(activeSessionIds, { signal }),
    enabled: probeEnabled && active.length > 0,
    refetchInterval: LOCAL_UPLOAD_STALL_CHECK_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!probe.data) return;
    setProbeExpired((previous) => {
      const next = new Set([...previous, ...probe.data.expiredSessionIds]);
      return sameSessionIds(previous, next) ? previous : next;
    });
    observations.current = observeGcsOffsets(
      observations.current,
      new Map(Object.entries(probe.data.offsets)),
      probe.data.observedAt,
      LOCAL_UPLOAD_STALL_CHECK_MS,
    );
    const next = interruptedSessionIds(observations.current);
    setInterrupted((previous) => (sameSessionIds(previous, next) ? previous : next));
  }, [probe.data]);

  const expiredSessionIds = useMemo(
    () => new Set([...probeExpired].filter((sessionId) => activeSessionIds.includes(sessionId))),
    [activeSessionIds, probeExpired],
  );
  const visibleInterruptedSessionIds = useMemo(
    () => new Set([...interrupted].filter((sessionId) => !expiredSessionIds.has(sessionId))),
    [expiredSessionIds, interrupted],
  );
  return { expiredSessionIds, interruptedSessionIds: visibleInterruptedSessionIds };
}

interface LiveUploadProgressSample {
  sessionId: string;
  offset: number;
}

/** Watch live local uploads without imposing an XHR timeout. After browser byte progress has
 * remained unchanged for the full stall window, query the authoritative GCS committed offset. An
 * equal/lower offset confirms the interruption; a failed probe remains inconclusive. */
export function useInterruptedLiveUploads(items: readonly UploadTask[]): LocalUploadHealth {
  const active = useMemo<LiveUploadProgressSample[]>(
    () =>
      items.flatMap((item) =>
        item.channel === 'upload' &&
        item.phase === 'uploading' &&
        item.backendId &&
        !item.needsReattach
          ? [{ sessionId: item.backendId, offset: item.bytes }]
          : [],
      ),
    [items],
  );
  const observations = useRef<LocalUploadProgressObservations>(new Map());
  const [observedAt, setObservedAt] = useState(() => Date.now());
  const [interrupted, setInterrupted] = useState<ReadonlySet<string>>(new Set());
  const [probeExpired, setProbeExpired] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const now = Date.now();
    const previousObservations = observations.current;
    const nextObservations = observeProgressOffsets(previousObservations, active, now);
    observations.current = nextObservations;
    const next = interruptedSessionIds(observations.current);
    setInterrupted((previous) => (sameSessionIds(previous, next) ? previous : next));
    if (!sameProgressObservations(previousObservations, nextObservations)) setObservedAt(now);
  }, [active]);

  const candidates = useMemo(
    () =>
      active.filter((sample) => {
        const observation = observations.current.get(sample.sessionId);
        return (
          observation !== undefined &&
          observedAt - observation.lastAdvancedAt >= LOCAL_UPLOAD_STALL_CHECK_MS
        );
      }),
    [active, observedAt],
  );
  const candidateSessionIds = useMemo(
    () => candidates.map((sample) => sample.sessionId),
    [candidates],
  );

  useEffect(() => {
    if (active.length === 0 || candidates.length > 0) return;
    const nextDeadline = Math.min(
      ...active.map(
        (sample) =>
          (observations.current.get(sample.sessionId)?.lastAdvancedAt ?? observedAt) +
          LOCAL_UPLOAD_STALL_CHECK_MS,
      ),
    );
    const timer = window.setTimeout(
      () => setObservedAt(Date.now()),
      Math.max(0, nextDeadline - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [active, candidates.length, observedAt]);

  const probe = useQuery({
    queryKey: [UPLOADS_KEY, 'live-upload-offsets', candidateSessionIds],
    queryFn: ({ signal }) => fetchLocalUploadOffsets(candidateSessionIds, { signal }),
    enabled: candidates.length > 0,
    refetchInterval: LOCAL_UPLOAD_STALL_PROBE_RETRY_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!probe.data) return;
    setProbeExpired((previous) => {
      const next = new Set([...previous, ...probe.data.expiredSessionIds]);
      return sameSessionIds(previous, next) ? previous : next;
    });
    observations.current = confirmLiveUploadStalls(
      observations.current,
      new Map(Object.entries(probe.data.offsets)),
      probe.data.observedAt,
      LOCAL_UPLOAD_STALL_CHECK_MS,
    );
    const next = interruptedSessionIds(observations.current);
    setInterrupted((previous) => (sameSessionIds(previous, next) ? previous : next));
    setObservedAt(probe.data.observedAt);
  }, [probe.data]);

  const expiredSessionIds = useMemo(
    () =>
      new Set(
        [...probeExpired].filter((sessionId) => active.some((row) => row.sessionId === sessionId)),
      ),
    [active, probeExpired],
  );
  const visibleInterruptedSessionIds = useMemo(
    () => new Set([...interrupted].filter((sessionId) => !expiredSessionIds.has(sessionId))),
    [expiredSessionIds, interrupted],
  );
  return { expiredSessionIds, interruptedSessionIds: visibleInterruptedSessionIds };
}

export function useMySubmissions(uid: string, bucket?: TransferHistoryQueryBucket) {
  return useInfiniteQuery({ ...mySubmissionsQuery(uid, bucket), select: selectSubmissionPages });
}

/** Resolve post-upload intake/MCAP outcomes for the durable history page. The uploader list only
 * carries the byte-transfer status, so an `uploaded` row needs this existing batch endpoint before
 * it can honestly render Processing, Ingested, or the final failure reason after a page refresh. */
export function useUploadHistoryStatuses(
  uid: string,
  submissions: readonly Submission[],
): ReadonlyMap<string, LocalSubmissionStatusItem> {
  const sessionIds = useMemo(
    () => [
      ...new Set(
        submissions
          .filter((submission) => submission.status === 'uploaded')
          .map((submission) => submission.sessionId),
      ),
    ],
    [submissions],
  );
  const mediaTypeById = useMemo(
    () =>
      new Map(
        submissions.map((submission) => [
          submission.sessionId,
          submission.mediaType === 'mcap' ? ('mcap' as const) : ('video' as const),
        ]),
      ),
    [submissions],
  );
  const cacheRef = useRef<{
    uid: string;
    values: Map<string, LocalSubmissionStatusItem>;
  }>({ uid, values: new Map() });
  const sessionIdsRef = useRef(sessionIds);
  const mediaTypeByIdRef = useRef(mediaTypeById);
  const requestSequence = useRef(0);
  const lastAppliedSequence = useRef(0);
  const previousIdSignature = useRef('');
  const [cacheRevision, setCacheRevision] = useState(0);
  if (cacheRef.current.uid !== uid) {
    cacheRef.current = { uid, values: new Map() };
    lastAppliedSequence.current = 0;
    previousIdSignature.current = '';
  }
  sessionIdsRef.current = sessionIds;
  mediaTypeByIdRef.current = mediaTypeById;
  const idSignature = sessionIds.join('\u0000');
  const query = useQuery({
    queryKey: [UPLOADS_KEY, 'upload-history-status', uid],
    queryFn: async ({ signal }) => {
      const sequence = ++requestSequence.current;
      const requestedIds = sessionIdsRef.current.filter((sessionId) => {
        const status = cacheRef.current.values.get(sessionId);
        if (!status) return true;
        const phase = deriveLocalStatusPatch(status, mediaTypeByIdRef.current.get(sessionId)).phase;
        return !phase || !isTerminalPhase(phase);
      });
      const statuses: LocalSubmissionStatusItem[] = [];
      for (let start = 0; start < requestedIds.length; start += LOCAL_UPLOAD_STATUS_BATCH_SIZE) {
        statuses.push(
          ...(await fetchLocalSubmissionsStatus(
            requestedIds.slice(start, start + LOCAL_UPLOAD_STATUS_BATCH_SIZE),
            { signal },
          )),
        );
      }
      return { requestSequence: sequence, statuses };
    },
    enabled: !!uid && sessionIds.length > 0,
    staleTime: UPLOADS_STALE_TIME,
    refetchInterval: () => {
      const hasUnresolved = sessionIdsRef.current.some((sessionId) => {
        const status = cacheRef.current.values.get(sessionId);
        if (!status) return true;
        const phase = deriveLocalStatusPatch(status, mediaTypeByIdRef.current.get(sessionId)).phase;
        return !phase || !isTerminalPhase(phase);
      });
      return hasUnresolved ? LOCAL_UPLOAD_STATUS_POLL_MS : false;
    },
  });

  useEffect(() => {
    if (!query.data || query.data.requestSequence < lastAppliedSequence.current) return;
    lastAppliedSequence.current = query.data.requestSequence;
    for (const status of query.data.statuses) {
      cacheRef.current.values.set(status.sessionId, status);
    }
    setCacheRevision((revision) => revision + 1);
  }, [query.data]);

  useEffect(() => {
    if (!uid || previousIdSignature.current === idSignature) return;
    const hadPreviousIds = previousIdSignature.current !== '';
    previousIdSignature.current = idSignature;
    if (hadPreviousIds) void query.refetch();
  }, [idSignature, query.refetch, uid]);

  void cacheRevision;
  return new Map(cacheRef.current.values);
}

/** Resolve post-transfer Video Intake / MCAP Ingest outcomes for durable Source Import history.
 * `SourceImport.state === transferred` only proves that the remote copy completed; the batch status
 * endpoint remains authoritative for downstream processing, duplicate/rejection, and asset results. */
export function useSourceImportHistoryStatuses(
  uid: string,
  imports: readonly SourceImport[],
): ReadonlyMap<string, SourceImportStatusItem> {
  const transferredImports = useMemo(
    () => imports.filter((sourceImport) => sourceImport.state === 'transferred'),
    [imports],
  );
  const importIds = useMemo(
    () => [...new Set(transferredImports.map((sourceImport) => sourceImport.importId))],
    [transferredImports],
  );
  const mediaTypeById = useMemo(
    () =>
      new Map(
        transferredImports.map((sourceImport) => [sourceImport.importId, sourceImport.mediaType]),
      ),
    [transferredImports],
  );
  const cacheRef = useRef<{
    uid: string;
    values: Map<string, SourceImportStatusItem>;
  }>({ uid, values: new Map() });
  const importIdsRef = useRef(importIds);
  const mediaTypeByIdRef = useRef(mediaTypeById);
  const requestSequence = useRef(0);
  const lastAppliedSequence = useRef(0);
  const previousIdSignature = useRef('');
  const [cacheRevision, setCacheRevision] = useState(0);
  if (cacheRef.current.uid !== uid) {
    cacheRef.current = { uid, values: new Map() };
    lastAppliedSequence.current = 0;
    previousIdSignature.current = '';
  }
  importIdsRef.current = importIds;
  mediaTypeByIdRef.current = mediaTypeById;
  const idSignature = importIds.join('\u0000');
  const query = useQuery({
    queryKey: [UPLOADS_KEY, 'source-import-history-status', uid],
    queryFn: async ({ signal }) => {
      const sequence = ++requestSequence.current;
      const requestedIds = importIdsRef.current.filter((importId) => {
        const status = cacheRef.current.values.get(importId);
        if (!status) return true;
        const patch = deriveStatusPatch(status, {
          channel: 'import',
          mediaType: mediaTypeByIdRef.current.get(importId),
        });
        return !patch.phase || !isTerminalPhase(patch.phase);
      });
      const statuses: SourceImportStatusItem[] = [];
      for (let start = 0; start < requestedIds.length; start += SOURCE_IMPORT_STATUS_BATCH_SIZE) {
        statuses.push(
          ...(await fetchSourceImportsStatus(
            requestedIds.slice(start, start + SOURCE_IMPORT_STATUS_BATCH_SIZE),
            { signal },
          )),
        );
      }
      return { requestSequence: sequence, statuses };
    },
    enabled: !!uid && importIds.length > 0,
    staleTime: UPLOADS_STALE_TIME,
    refetchInterval: (statusQuery) => {
      if (!statusQuery.state.data) return SOURCE_IMPORT_STATUS_POLL_MS;
      const hasUnresolvedImport = importIdsRef.current.some((importId) => {
        const status = cacheRef.current.values.get(importId);
        // The endpoint intentionally echoes only recognized ids. A missing old history id has no
        // lifecycle evidence to project, so leave it as Transferred instead of polling forever.
        if (!status) return false;
        const patch = deriveStatusPatch(status, {
          channel: 'import',
          mediaType: mediaTypeByIdRef.current.get(importId),
        });
        return !patch.phase || !isTerminalPhase(patch.phase);
      });
      return hasUnresolvedImport ? SOURCE_IMPORT_STATUS_POLL_MS : false;
    },
  });

  useEffect(() => {
    if (!query.data || query.data.requestSequence < lastAppliedSequence.current) return;
    lastAppliedSequence.current = query.data.requestSequence;
    for (const status of query.data.statuses) {
      cacheRef.current.values.set(status.importId, status);
    }
    setCacheRevision((revision) => revision + 1);
  }, [query.data]);

  useEffect(() => {
    if (!uid || previousIdSignature.current === idSignature) return;
    const hadPreviousIds = previousIdSignature.current !== '';
    previousIdSignature.current = idSignature;
    if (hadPreviousIds) void query.refetch();
  }, [idSignature, query.refetch, uid]);

  void cacheRevision;
  return new Map(cacheRef.current.values);
}

/** Refresh the low-frequency server lists when a browser transfer gains an id or changes lifecycle
 * phase. The signature excludes byte progress, so active uploads do not invalidate per chunk. */
export function useSyncUploadHistory(uid: string, items: readonly UploadTask[]): void {
  const queryClient = useQueryClient();
  const uploadSignature = uploadHistorySignature(items, 'upload');
  const importSignature = uploadHistorySignature(items, 'import');
  const previousUpload = useRef<{ uid: string; signature: string } | undefined>(undefined);
  const previousImport = useRef<{ uid: string; signature: string } | undefined>(undefined);

  useEffect(() => {
    const previous = previousUpload.current;
    previousUpload.current = { uid, signature: uploadSignature };
    if (!uid || (!uploadSignature && !(previous?.uid === uid && previous.signature))) return;
    void queryClient.invalidateQueries({
      queryKey: [UPLOADS_KEY, 'my-submissions', uid],
    });
  }, [queryClient, uid, uploadSignature]);

  useEffect(() => {
    const previous = previousImport.current;
    previousImport.current = { uid, signature: importSignature };
    if (!uid || (!importSignature && !(previous?.uid === uid && previous.signature))) return;
    void queryClient.invalidateQueries({
      queryKey: [UPLOADS_KEY, 'my-source-imports', uid],
    });
  }, [queryClient, uid, importSignature]);
}

export function mySourceImportsQuery(uid: string, bucket?: TransferHistoryQueryBucket) {
  return infiniteQueryOptions({
    queryKey: [UPLOADS_KEY, 'my-source-imports', uid, bucket ?? 'all'],
    queryFn: ({ pageParam, signal }) =>
      listMySourceImports(uid, { limit: 100, cursor: pageParam, bucket }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!uid,
    staleTime: UPLOADS_STALE_TIME,
    refetchInterval: UPLOADS_HISTORY_REFRESH_MS,
    // No `placeholderData: keepPreviousData` here, deliberately: the only varying part of this key is
    // `uid`, so a key change is an account switch, not a filter edit — carrying the previous data over
    // would render one account's transfer history under another for a beat.
    throwOnError: (_error, query) =>
      shouldEscalateToRouteBoundary(query.state.error, query.state.data !== undefined),
  });
}

export function useMySourceImports(uid: string, bucket?: TransferHistoryQueryBucket) {
  return useInfiniteQuery({
    ...mySourceImportsQuery(uid, bucket),
    select: selectSourceImportPages,
  });
}
